import { Card, Col, Descriptions, Form, InputNumber, Row, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { money } from '../utils/format';

const DAYS_PER_MONTH = 30;

/**
 * Mirrors the server formula exactly: daily profit = (principal × rate ÷ 100) ÷ 30.
 * Computed client-side so the result updates as you type; /api/calculator returns the same numbers.
 */
export default function CalculatorPage() {
  const [principal, setPrincipal] = useState<number>(100000);
  const [rate, setRate] = useState<number>(2.3);
  const [days, setDays] = useState<number>(30);

  const result = useMemo(() => {
    const daily = (principal * rate) / 100 / DAYS_PER_MONTH;
    return {
      daily,
      total: daily * days,
      monthly: daily * DAYS_PER_MONTH,
      yearly: daily * 365,
    };
  }, [principal, rate, days]);

  const valid = principal > 0 && rate >= 0.01 && rate <= 99.99 && days >= 0;

  return (
    <>
      <Typography.Title level={3}>Profit calculator</Typography.Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={10}>
          <Card title="Inputs">
            <Form layout="vertical">
              <Form.Item label="Principal (PKR)">
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  value={principal}
                  onChange={(v) => setPrincipal(v ?? 0)}
                />
              </Form.Item>
              <Form.Item
                label="Monthly profit rate (%)"
                help="Between 0.01 and 99.99"
                validateStatus={rate >= 0.01 && rate <= 99.99 ? undefined : 'error'}
              >
                <InputNumber
                  min={0.01}
                  max={99.99}
                  step={0.01}
                  style={{ width: '100%' }}
                  value={rate}
                  onChange={(v) => setRate(v ?? 0)}
                />
              </Form.Item>
              <Form.Item label="Days">
                <InputNumber min={0} style={{ width: '100%' }} value={days} onChange={(v) => setDays(v ?? 0)} />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card title="Result">
            {valid ? (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Daily profit">PKR {money(result.daily)}</Descriptions.Item>
                <Descriptions.Item label={`Profit over ${days} day(s)`}>
                  <strong style={{ fontSize: 18 }}>PKR {money(result.total)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Per 30-day month">PKR {money(result.monthly)}</Descriptions.Item>
                <Descriptions.Item label="Per 365 days">PKR {money(result.yearly)}</Descriptions.Item>
              </Descriptions>
            ) : (
              <Typography.Text type="danger">Enter a valid principal, rate and day count.</Typography.Text>
            )}

            <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
              Formula: (principal × rate ÷ 100) ÷ 30 × days
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>
    </>
  );
}
